/**
 * ONEFOLD scroll reveal.
 *
 * - Elements with [data-of-reveal] animate in when they enter the viewport.
 *   Siblings inside the same [data-of-reveal-group] are staggered automatically.
 * - Any other section in <main> / footer (e.g. stock Horizon sections) is revealed as a whole.
 *
 * The html element gets .of-anim from an inline script in snippets/onefold-head.liquid,
 * so content is only hidden when this script is going to run.
 */
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const STAGGER_MS = 110;

  window.onefoldRevealReady = true;

  if (reduceMotion || !('IntersectionObserver' in window)) {
    root.classList.remove('of-anim');
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add('is-revealed');
        observer.unobserve(entry.target);
      }
    },
    { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
  );

  /** @param {ParentNode} scope */
  function setup(scope) {
    // Stagger items within each group.
    for (const group of scope.querySelectorAll('[data-of-reveal-group]')) {
      const items = group.querySelectorAll('[data-of-reveal]');
      items.forEach((item, i) => {
        if (item instanceof HTMLElement && !item.style.getPropertyValue('--of-delay')) {
          item.style.setProperty('--of-delay', `${i * STAGGER_MS}ms`);
        }
      });
    }

    // Stock sections without their own reveal markup animate as one block.
    // The first section in <main> (e.g. the product page's buy area) is above the fold
    // and must appear immediately, so it is never hidden.
    const sections = scope.querySelectorAll('main .shopify-section, footer .shopify-section');
    for (const section of sections) {
      const isFirstInMain = section.parentElement?.matches('main') && !section.previousElementSibling;
      if (!isFirstInMain && !section.querySelector('[data-of-reveal]')) {
        section.setAttribute('data-of-reveal', 'section');
      }
    }

    for (const el of scope.querySelectorAll('[data-of-reveal]:not(.is-revealed)')) {
      observer.observe(el);
    }
  }

  function init() {
    // Wait a frame so elements already in view still transition in.
    requestAnimationFrame(() => requestAnimationFrame(() => setup(document)));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders sections; reveal them immediately.
  document.addEventListener('shopify:section:load', (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    setup(target);
    for (const el of target.querySelectorAll('[data-of-reveal]')) el.classList.add('is-revealed');
    target.classList.add('is-revealed');
  });
})();
